import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';

const EmojiPicker = ({ onEmojiSelect, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Smileys & People');
  const searchInputRef = useRef(null);
  const pickerRef = useRef(null);

  // WhatsApp-grade emoji collection with categories
  const emojiCategories = {
    'Smileys & People': [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇',
      '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑',
      '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬',
      '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶',
      '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '👍', '👎', '👏', '🙌', '👐',
      '🤲', '🤝', '🙏', '✋', '🤚', '🖐', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘',
      '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤟', '🖤', '🤍', '🤎'
    ],
    'Animals & Nature': [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷',
      '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆',
      '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜',
      '🌸', '🌺', '🌻', '🌹', '🥀', '🌷', '🌼', '🌵', '🌲', '🌳', '🌴', '🌱', '🌿',
      '☘️', '🍀', '🎍', '🎋', '🍃', '🍂', '🍁', '🍄', '🐚', '🌾', '💐', '🌷', '🌹'
    ],
    'Food & Drink': [
      '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭',
      '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶', '🫑', '🌽', '🥕',
      '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳',
      '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓',
      '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🥫', '🍝', '🍜', '🍲',
      '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢',
      '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿',
      '🍩', '🍪', '🌰', '🥜', '🍯', '☕', '🍵', '🧃', '🥤', '🧋', '🍶', '🍾', '🍷',
      '🍸', '🍹', '🍺', '🍻', '🥂', '🥃', '🥤', '🧊', '🥢', '🍽️', '🍴', '🥄'
    ],
    'Objects': [
      '📱', '💻', '⌨️', '🖥', '🖨', '🖱', '💽', '💾', '💿', '📀', '📼', '📷', '📸',
      '📹', '🎥', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚', '🎛', '🧭', '⏱',
      '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯', '🪔', '🧯',
      '🛢', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🧰', '🔧', '🔨',
      '⚒', '🛠', '⛏', '🔩', '⚙️', '🧱', '⛓', '🧲', '🔫', '💣', '🧨', '🪓', '🔪',
      '🗡', '⚔️', '🛡', '🚬', '⚰️', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭',
      '🔬', '🕳', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡', '🧹'
    ],
    'Symbols': [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞',
      '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉', '☸️', '✡️', '🔯',
      '🕎', '☯️', '☦️', '🛐', '⚛️', '🕎', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
      '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵',
      '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛',
      '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕',
      '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️',
      '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾',
      '♿', '🅿️', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚡', '🔥'
    ]
  };

  const allEmojis = Object.values(emojiCategories).flat();

  // Enhanced search with common emoji names
  const emojiNames = {
    '😀': 'happy smile grin face joy',
    '😍': 'love heart eyes adore crush',
    '😂': 'laugh crying joy funny lol tears',
    '👍': 'thumbs up like good approve yes',
    '❤️': 'heart love red romance',
    '🔥': 'fire hot flame lit amazing',
    '💯': 'hundred perfect score excellent',
    '🎉': 'party celebration confetti hooray',
    '👏': 'clap applause good job bravo',
    '🙏': 'pray thanks please namaste',
    '😭': 'crying sad tears upset',
    '😊': 'happy smile blush pleased',
    '😘': 'kiss love blow kiss',
    '🤔': 'thinking hmm consider ponder',
    '😎': 'cool sunglasses awesome',
    '🙄': 'eye roll annoyed whatever',
    '😴': 'sleep tired sleepy zzz',
    '🤩': 'star eyes amazed wow excited',
    '😅': 'sweat smile nervous awkward',
    '🥰': 'love hearts adore sweet'
  };

  const filteredEmojis = searchTerm 
    ? allEmojis.filter(emoji => {
        const name = emojiNames[emoji] || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : emojiCategories[activeCategory] || allEmojis.slice(0, 48);

  const categoryIcons = {
    'Smileys & People': '😊',
    'Animals & Nature': '🐶',
    'Food & Drink': '🍎',
    'Objects': '📱',
    'Symbols': '❤️'
  };

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleEmojiClick = (emoji) => {
    onEmojiSelect?.(emoji);
    onClose?.();
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      <AnimatePresence>
        <motion.div
          ref={pickerRef}
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          className="fixed bottom-20 left-4 right-4 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 md:w-72 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-xl z-50"
        >
          {/* Header with search */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Icon name="Search" size={14} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/50" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search emojis..."
                className="w-full pl-8 pr-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none text-xs"
              />
            </div>
          </div>

          {/* Category tabs */}
          {!searchTerm && (
            <div className="flex p-1 border-b border-white/10 gap-1">
              {Object.keys(emojiCategories).map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`flex-1 p-1.5 text-base rounded-md transition-colors ${
                    activeCategory === category
                      ? 'bg-primary/20 text-primary'
                      : 'hover:bg-white/10'
                  }`}
                  title={category}
                >
                  {categoryIcons[category]}
                </button>
              ))}
            </div>
          )}

          {/* Emoji grid */}
          <div className="p-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            <div className="grid grid-cols-8 gap-0.5">
              {filteredEmojis.slice(0, 48).map((emoji, index) => (
                <motion.button
                  key={`${emoji}-${index}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleEmojiClick(emoji)}
                  className="w-7 h-7 rounded-md hover:bg-white/20 transition-colors flex items-center justify-center text-base"
                  title={emoji}
                >
                  {emoji}
                </motion.button>
              ))}
            </div>
            
            {filteredEmojis.length === 0 && (
              <div className="text-center py-3 text-white/60 text-xs">
                No emojis found for "{searchTerm}"
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-1.5 border-t border-white/10 text-center">
            <span className="text-white/40 text-xs">Tap emoji to add</span>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default EmojiPicker;